// Socket.io event name constants — single source of truth for client and server

// Client → Server
export const CLIENT_EVENTS = {
  CREATE_LOBBY: 'create-lobby',
  JOIN_LOBBY: 'join-lobby',
  SETUP_PLAYER: 'setup-player',
  UPDATE_COEFFICIENT: 'update-coefficient',
  START_SPIN: 'start-spin',
  RESET_GAME: 'reset-game',
  LEAVE_LOBBY: 'leave-lobby',
  LAUNCH_MINIGAME: 'launch-minigame',
  SUBMIT_MINIGAME_ANSWER: 'submit-minigame-answer',
  CLOSE_MINIGAME_RESULTS: 'close-minigame-results',
} as const;

// Server → Client
export const SERVER_EVENTS = {
  LOBBY_CREATED: 'lobby-created',
  LOBBY_JOINED: 'lobby-joined',
  PLAYER_UPDATED: 'player-updated',
  PLAYER_LEFT: 'player-left',
  SPIN_STARTED: 'spin-started',
  COUNTDOWN_STARTED: 'countdown-started',
  GAME_RESET: 'game-reset',
  ADMIN_TRANSFERRED: 'admin-transferred',
  ERROR: 'error',
  MINIGAME_STARTED: 'minigame-started',
  MINIGAME_ANSWER_RECEIVED: 'minigame-answer-received',
  MINIGAME_RESULTS: 'minigame-results',
  MINIGAME_ENDED: 'minigame-ended',
} as const;

export type ClientEventName = typeof CLIENT_EVENTS[keyof typeof CLIENT_EVENTS];
export type ServerEventName = typeof SERVER_EVENTS[keyof typeof SERVER_EVENTS];

// ── Minigame types ────────────────────────────────────────────────────────────

export type MinigameType =
  | 'closest-number'
  | 'number-question'
  | 'pick-a-side'
  | 'fastest-tap'
  | 'type-the-word'
  | 'vote-boost'
  | 'team-red-blue';

export interface MinigameResult {
  playerId: string;
  rank: number;
  scoreLabel: string;
  coeffDiff: number;
  newCoefficient: number;
}

export interface MinigameStateSnapshot {
  type: MinigameType;
  phase: 'countdown' | 'active' | 'results';
  question?: string;
  direction?: 'highest' | 'lowest';
  options?: [string, string];
  readyAt?: number;
  word?: string;
  deadline: number;
  answeredCount: number;
  totalCount: number;
  targetNumber?: number;
  results?: MinigameResult[];
}

// ── Payload types for events ──────────────────────────────────────────────────

export interface PlayerSnapshot {
  id: string;
  name: string;
  avatarType: 'bundled' | 'custom';
  avatar: string;
  coefficient: number;
  color: string;
  isAdmin: boolean;
  isConnected: boolean;
}

export interface LobbySnapshot {
  id: string;
  adminId: string;
  players: PlayerSnapshot[];
  phase: 'waiting' | 'countdown' | 'spinning' | 'result';
  winner: PlayerSnapshot | null;
  minigameState?: MinigameStateSnapshot;
}

// Client → Server payloads
export interface CreateLobbyPayload { sessionToken: string; }
export interface JoinLobbyPayload { lobbyId: string; sessionToken: string; }
export interface SetupPlayerPayload {
  lobbyId: string;
  name: string;
  avatarType: 'bundled' | 'custom';
  avatar: string;
}
export interface UpdateCoefficientPayload {
  lobbyId: string;
  targetPlayerId: string;
  coefficient: number;
}
export interface StartSpinPayload { lobbyId: string; }
export interface ResetGamePayload { lobbyId: string; }
export interface LeaveLobbyPayload { lobbyId: string; }
export interface LaunchMinigamePayload {
  lobbyId: string;
  gameType: MinigameType;
  questionId?: string;
}
export interface SubmitMinigameAnswerPayload {
  lobbyId: string;
  answer: number | string;
}
export interface CloseMinigameResultsPayload { lobbyId: string; }

// Server → Client payloads
export interface LobbyCreatedPayload { lobbyId: string; lobby: LobbySnapshot; }
export interface LobbyJoinedPayload { lobby: LobbySnapshot; }
export interface PlayerUpdatedPayload { player: PlayerSnapshot; }
export interface PlayerLeftPayload { playerId: string; }
export interface SpinStartedPayload {
  winner: PlayerSnapshot;
  finalAngle: number;
  duration: number;
}
export interface GameResetPayload { lobby: LobbySnapshot; }
export interface AdminTransferredPayload { newAdminId: string; }
export interface ErrorPayload { code: string; message: string; }
export interface MinigameStartedPayload { minigame: MinigameStateSnapshot; }
export interface MinigameAnswerReceivedPayload { answeredCount: number; totalCount: number; }
export interface MinigameResultsPayload {
  minigame: MinigameStateSnapshot;
  updatedPlayers: PlayerSnapshot[];
}
export interface MinigameEndedPayload { lobby: LobbySnapshot; }
